import React, { useState } from 'react'
import {
  Container,
  Eyebrow,
  SectionTitle,
  Lede,
  Card,
  IconBadge,
} from '../AumhumSite/sections'
import {
  ArrowRight,
  Baby,
  Brain,
  ChevronDown,
  HeartPulse,
  Puzzle,
  ShieldCheck,
  Stethoscope,
} from '../AumhumSite/icons'
import { PATHWAYS, resolvePathway } from './constants'
import StepDots from './StepDots'

const ICONS = {
  Brain,
  HeartPulse,
  ShieldCheck,
  Baby,
  Puzzle,
  Stethoscope,
}

// Step 1 of the prospect signup. The user picks the kind of care they need;
// "Mental health" expands inline into a sub-pick (Psychiatry vs Therapy).
//
// On submit (`onPick`) we hand back the resolved `{ pathway, specialtyTag,
// topLabel, subLabel }`. The orchestrator owns the next step.
export default function PathwayPicker({ initialTopId = null, initialSubId = null, onPick }) {
  const [topId, setTopId] = useState(initialTopId)
  const [subId, setSubId] = useState(initialSubId)
  const top = PATHWAYS.find((p) => p.id === topId) || null
  const ready = topId && (!top?.subOptions || subId)

  const handleSubmit = () => {
    const resolved = resolvePathway(topId, subId)
    if (resolved && onPick) onPick({ topId, subId, ...resolved })
  }

  return (
    <Container className="pps-shell">
      <StepDots step={1} of={3} />
      <Eyebrow>Get matched</Eyebrow>
      <SectionTitle as="h1">What kind of care are you looking for?</SectionTitle>
      <Lede>
        Pick what fits best. We&rsquo;ll use this to find you the right specialist —
        you can refine details after you create your account.
      </Lede>

      <div className="pps-pathway-grid">
        {PATHWAYS.map((p) => {
          const Icon = ICONS[p.iconName] || Stethoscope
          const isActive = topId === p.id
          return (
            <Card
              key={p.id}
              className={`pps-pathway-card${isActive ? ' is-active' : ''}`}
              emphasised={isActive}
            >
              <button
                type="button"
                className="pps-pathway-button"
                aria-pressed={isActive}
                aria-expanded={isActive && !!p.subOptions}
                onClick={() => {
                  setTopId(p.id)
                  setSubId(null)
                }}
              >
                <span className="pps-pathway-icon">
                  <IconBadge Icon={Icon} variant={isActive ? 'green' : 'violet'} />
                </span>
                <span className="pps-pathway-text">
                  <span className="pps-pathway-label">{p.label}</span>
                  <span className="pps-pathway-blurb">{p.blurb}</span>
                </span>
                {p.subOptions ? (
                  <span className="pps-pathway-chev" aria-hidden>
                    <ChevronDown />
                  </span>
                ) : null}
              </button>

              {isActive && p.subOptions ? (
                <div className="pps-suboptions" role="radiogroup" aria-label={`${p.label} options`}>
                  {p.subOptions.map((s) => {
                    const subActive = subId === s.id
                    return (
                      <button
                        key={s.id}
                        type="button"
                        role="radio"
                        aria-checked={subActive}
                        className={`pps-subopt${subActive ? ' is-active' : ''}`}
                        onClick={() => setSubId(s.id)}
                      >
                        <span className="pps-subopt-label">{s.label}</span>
                        <span className="pps-subopt-blurb">{s.blurb}</span>
                      </button>
                    )
                  })}
                </div>
              ) : null}
            </Card>
          )
        })}
      </div>

      <div className="pps-cta-row">
        <button
          type="button"
          className={`pps-btn is-primary${ready ? '' : ' is-disabled'}`}
          disabled={!ready}
          onClick={handleSubmit}
        >
          Continue <ArrowRight />
        </button>
      </div>
    </Container>
  )
}
